import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, X } from "lucide-react";

const daysBetween = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));

export const SellerTrialBanner = ({ user, onRefresh }) => {
  const [cancelling, setCancelling] = useState(false);
  if (!user || user.subscription_status !== "trial") return null;

  const trialEnds = user.trial_ends_at;
  const cancelDeadline = user.trial_cancel_deadline;
  const canCancel = cancelDeadline ? Date.now() < new Date(cancelDeadline).getTime() : false;
  const cancelDaysLeft = cancelDeadline ? daysBetween(cancelDeadline) : 0;

  const cancel = async () => {
    if (!window.confirm("Cancel your free trial? Your store will be paused. This can only be done within the first 14 days.")) return;
    setCancelling(true);
    try {
      await api.post("/seller/trial/cancel");
      toast.success("Free trial cancelled. Your store has been paused.");
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel trial"); }
    finally { setCancelling(false); }
  };

  return (
    <div data-testid="seller-trial-banner" className="mb-5 rounded-2xl border border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5" /></span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">You're on your free trial 🎉</p>
        <p className="text-xs text-muted-foreground">
          {trialEnds ? `Trial ends ${new Date(trialEnds).toLocaleDateString()}. ` : ""}
          {canCancel
            ? `You can cancel free within the first 14 days (${cancelDaysLeft} day${cancelDaysLeft !== 1 ? "s" : ""} left).`
            : "The 14-day cancellation window has passed — contact admin to deactivate your store."}
        </p>
      </div>
      {canCancel && (
        <Button data-testid="cancel-trial-btn" variant="outline" size="sm" className="rounded-full shrink-0" disabled={cancelling} onClick={cancel}>
          <X className="w-4 h-4 mr-1" />{cancelling ? "Cancelling…" : "Cancel free trial"}
        </Button>
      )}
    </div>
  );
};
